<template>
    <v-app id="inspire" :class="{ 'global-bold': user_settings.accessibility_mode }">
        <!-- Skip link -->
        <a href="#content" class="sr-only sr-only-focusable" @click.prevent="focusMain">Skip to main content</a>

        <!-- Announcements -->
        <v-snackbar top timeout="30000" v-model="showUpdateMessage" color="#fcba03" role="status" aria-live="polite" aria-atomic="true">
            <v-icon aria-hidden="true">mdi-update</v-icon>
            <span class="font-weight-bold pl-2"> A new update has been released. Reloading application in {{ timer }} seconds! </span>
        </v-snackbar>

        <v-overlay :value="offline" aria-label="Connection status">
            <div class="pb-0" role="alert" aria-live="assertive">
                <div class="download-label">
                    <span class="mx-auto">Connection lost. Reconnecting...</span>
                </div>
                <v-progress-linear
                    style="width: 310px"
                    label="Reconnecting"
                    color="white"
                    indeterminate
                    rounded
                    height="6"
                    role="progressbar"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    aria-label="Reconnecting"></v-progress-linear>
            </div>
        </v-overlay>

        <!-- left nav -->
        <v-navigation-drawer v-if="usermail" v-model="drawer" dark app clipped :expand-on-hover="expand" id="nav" role="navigation" aria-label="Primary navigation">
            <!-- Research Suite -->
            <div v-if="$store.state.research_suite" class="nav-section">
                <v-list-group v-model="researchnav" prepend-icon="mdi-magnify" append-icon="mdi-chevron-down" color="white" class="cs_nav pl-0" aria-label="Research Suite">
                    <template v-slot:activator>
                        <v-list-item-title style="white-space: nowrap" class="white--text"> Research Suite </v-list-item-title>
                    </template>

                    <v-list shaped class="pl-0 pr-1" dense>
                        <v-list-item
                            color="white"
                            v-for="item in items"
                            :key="item.text"
                            link
                            :to="item.redirect"
                            @click="item.model = false"
                            class="navitem pl-10"
                            :aria-current="ariaCurrent(item.redirect)">
                            <v-list-item-icon>
                                <v-icon :class="!expand ? 'pl-3' : 'pl-0'" size="20px" class="">{{ item.icon }}</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>
                                    {{ item.text }}
                                </v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-list-group>
            </div>

            <!-- Content Suite -->
            <div v-if="showContentSuiteNav" class="nav-section">
                <v-list-group prepend-icon="mdi-text-box-edit" append-icon="mdi-chevron-down" color="white" shaped class="cs_nav pl-0" aria-label="Content Suite">
                    <template v-slot:activator>
                        <v-list-item-title class="white--text"> Content Suite </v-list-item-title>
                    </template>

                    <v-list shaped class="pl-0 pr-1" dense>
                        <v-list-item to="/content-projects" color="white" class="navitem pl-10" :aria-current="ariaCurrent('/content-projects', true)">
                            <v-list-item-icon>
                                <v-icon :class="!expand ? 'pl-3' : 'pl-0'" size="20px">mdi-text-box-multiple</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title> Projects</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="$store.state.content_suite_group.id == 1 || $store.state.admin" to="/wdf-idf/" color="white" class="navitem pl-10" :aria-current="ariaCurrent('/wdf-idf')">
                            <v-list-item-icon>
                                <v-icon :class="!expand ? 'pl-3' : 'pl-0'" size="20px">mdi-text-recognition</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>WDF*IDF</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-list-group>
            </div>

            <!-- QuickSEO -->
            <div v-if="showQuickSeoNav" class="nav-section">
                <v-list-group prepend-icon="mdi-chart-timeline-variant" append-icon="mdi-chevron-down" color="white" shaped class="cs_nav pl-0" aria-label="QuickSEO">
                    <template v-slot:activator>
                        <v-list-item-title class="white--text"> QuickSEO</v-list-item-title>
                    </template>

                    <v-list shaped class="pl-0 pr-1" dense>
                        <v-list-item shaped to="/quickseo/monitoring" color="white" class="navitem pl-10">
                            <v-list-item-icon>
                                <v-icon>mdi-view-dashboard</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>Monitoring</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item rounded to="/quickseo/internal-searches" color="white" class="navitem pl-10">
                            <v-list-item-icon>
                                <v-icon>mdi-store-search</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>Internal Searches</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-list-group>
            </div>
            <!-- Monitoring Suite (einfach, ohne QuickSEO) -->
            <div v-else-if="showMonitoringNav" class="nav-section">
                <v-list-item shaped to="/monitoring" color="white" class="navitem pl-10" :aria-current="ariaCurrent('/monitoring')">
                    <v-list-item-icon>
                        <v-icon>mdi-chart-timeline-variant</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>Monitoring Suite</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </div>

            <!-- AI Suite -->
            <div v-if="showAiSuiteNav" class="nav-section">
                <!-- active state controlled explicitly: /ai-suite pages except the SEO Audit sibling (/ai-suite/audit). "exact" stops
                     router-link's prefix matching; the :class binding re-adds the active state for non-audit /ai-suite subpages. -->
                <v-list-item
                    rounded
                    exact
                    to="/ai-suite"
                    color="white"
                    class="navitem pl-10"
                    :class="{ 'v-list-item--active': $route.path.startsWith('/ai-suite') && !$route.path.startsWith('/ai-suite/audit') }"
                    :input-value="$route.path.startsWith('/ai-suite') && !$route.path.startsWith('/ai-suite/audit')"
                    :aria-current="$route.path.startsWith('/ai-suite') && !$route.path.startsWith('/ai-suite/audit') ? 'page' : null">
                    <v-list-item-icon>
                        <v-icon>mdi-star-four-points</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>
                            <span>AI Suite</span>
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
                <!-- Page Audit vorerst ausgeblendet (Entscheidung Malte 2026-10-19); Route /ai-suite/audit bleibt erreichbar -->
                <v-list-item v-if="false" rounded to="/ai-suite/audit" color="white" class="navitem pl-10" :aria-current="ariaCurrent('/ai-suite/audit')">
                    <v-list-item-icon>
                        <v-icon>mdi-file-search-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>
                            <span>Page Audit</span>
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </div>

            <!-- AI Chat -->
            <div v-if="showAiChatNav" class="nav-section">
                <v-list-item rounded to="/seo-ai" color="white" class="navitem pl-10" :aria-current="ariaCurrent('/seo-ai')">
                    <v-list-item-icon>
                        <v-icon>mdi-message-text</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>
                            <span>AI Chat</span>
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </div>

            <!-- Internal Searches -->
            <div v-if="showInternalSearchesNav" class="nav-section">
                <v-list-item rounded to="/internal-searches" color="white" class="navitem pl-10" :aria-current="ariaCurrent('/internal-searches', true)">
                    <v-list-item-icon>
                        <v-icon>mdi-store-search</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>Internal Searches</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </div>
            <template v-slot:append>
                <!-- Advanced switch group -->
                <div style="width: 100%">
                    <div v-if="$helpers.isActionAllowed(42, user_rights)" :class="[expand ? 'switch-holder' : 'switch-holder switch-holder-expand', advanced_switch ? 'switch-holder--active' : '']">
                        <v-switch
                            @change="switchAdvancedMode()"
                            class="my-auto mode_switch"
                            v-model="advanced_switch"
                            dark
                            inset
                            color="white"
                            hide-details
                            dense
                            aria-label="Toggle advanced mode"></v-switch>
                        <div :class="expand ? 'switch-label' : 'switch-label expand'">
                            <span style="white-space: nowrap">Advanced Mode</span>
                        </div>
                    </div>
                    <v-list shaped nav class="pl-0 pr-1">
                        <v-list-item class="" link to="/knowledge" :aria-current="ariaCurrent('/knowledge', true)">
                            <v-list-item-action>
                                <v-icon class="pl-2" color="grey lighten-2">mdi-video</v-icon>
                            </v-list-item-action>
                            <v-list-item-title class="white--text text--darken-1 subnavtext">Tutorial</v-list-item-title>
                        </v-list-item>

                        <v-list-item v-if="$store.state.show_settings_page && $store.getters.canOpenSettings" link to="/settings" :aria-current="ariaCurrent('/settings')">
                            <v-list-item-action>
                                <v-icon class="pl-2" color="grey lighten-2">mdi-cog</v-icon>
                            </v-list-item-action>
                            <v-list-item-title class="white--text text--darken-1 subnavtext">Settings</v-list-item-title>
                        </v-list-item>

                        <v-list-item link to="/logout">
                            <v-list-item-action>
                                <v-icon class="pl-2" color="grey lighten-2">mdi-logout-variant</v-icon>
                            </v-list-item-action>
                            <v-list-item-title class="white--text text--darken-1 subnavtext">Logout</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </div>
            </template>
        </v-navigation-drawer>

        <!-- top nav -->
        <v-app-bar app clipped-left v-if="usermail" id="nav2" height="64px" role="banner" aria-label="Application bar">
            <div class="burger-wrapper">
                <input type="checkbox" id="hamburg" @click.stop="shownav" :aria-expanded="Boolean(drawer).toString()" aria-controls="nav" aria-label="Toggle navigation menu" />
                <label for="hamburg" class="hamburg" aria-hidden="true">
                    <span class="line"></span>
                    <span class="line"></span>
                    <span class="line"></span>
                </label>
            </div>

            <v-toolbar-title id="tooltitle" class="align-center" aria-label="Go to start page">
                <button class="tooltitle-btn" type="button" @click="$route.path !== '/' ? $router.push('/') : idle" aria-label="To start page">
                    <div id="logo-div" v-if="$route.path.substring(0, 9) === '/quickseo'">
                        <img id="logo" :src="logoSourceLutz" alt="QuickSEO logo" />
                    </div>
                    <div id="logo-div" v-else-if="company_id == 1547">
                        <img id="logo" :src="apollonsrc" alt="Apollon logo" />
                    </div>
                    <div id="logo-div" v-else>
                        <img id="logo" :src="logoSource" alt="Quaro logo" />
                    </div>
                </button>
            </v-toolbar-title>

            <!-- <v-btn
        id="updatebtn"
        dark
        small
        @click="
            user_settings.show_update_message = true
            $store.dispatch('CHANGE_USER_SETTINGS', user_settings)
        ">
        <v-icon small class="ml-0 mr-1">mdi-bell</v-icon> New Update</v-btn
    >
    <KIChat class="mr-2" /> -->

            <v-spacer id="top_nav"></v-spacer>
            <CompanySwitch class="ma-auto"></CompanySwitch>
            <!-- checkout pricing page -->
            <!-- <checkout v-if="usermail && company_status == 'testing'"></checkout> -->
            <v-btn icon class="mr-1" v-if="usermail" id="" @click="$router.push('/pricing')" aria-label="Open pricing">
                <v-icon class="" color="grey darken-2" aria-hidden="true">mdi-chess-queen</v-icon>
            </v-btn>
            <checkout-success v-if="showCheckoutSuccess"></checkout-success>
            <!-- OMR Review -->
            <!-- <OmrReview v-if="usermail && company_status != 'testing'" class="ml-5"></OmrReview> -->
            <!-- update card -->
            <!-- update card -->
            <update-card
                @close="
                    user_settings.show_update_message = false
                    $store.dispatch('CHANGE_USER_SETTINGS', user_settings)
                "
                :show="user_settings.show_update_message"
                v-if="usermail"></update-card>
            <!-- notifications -->
            <notifications
                v-if="usermail"
                @showUpdateInfo="
                    user_settings.show_update_message = true
                    $store.dispatch('CHANGE_USER_SETTINGS', user_settings)
                "></notifications>

            <div class="vl"></div>
            <v-menu ref="userMenu" :open-on-hover="false" :close-on-content-click="false" offset-y transition="slide-y-transition" bottom aria-label="User menu">
                <template v-slot:activator="{ on, attrs }">
                    <button
                        id="usersection"
                        v-bind="attrs"
                        v-on="on"
                        type="button"
                        class="user-activator"
                        aria-haspopup="menu"
                        :aria-expanded="$refs.userMenu && $refs.userMenu.isActive ? 'true' : 'false'"
                        aria-controls="user-menu-list">
                        <v-row no-gutters class="my-auto py-auto">
                            <v-col class="pr-2">
                                <v-avatar color="primary" size="38" class="pr-0 avat my-auto">
                                    <span class="initials">{{ initials }}</span>
                                </v-avatar>
                            </v-col>
                            <v-col id="userdata_nav" class="pr-0">
                                <div class="namepart">
                                    <span>{{ decryptedName }}</span>
                                </div>
                                <div class="mailpart pb-0">
                                    <span>{{ decryptedMail }}</span>
                                </div>
                            </v-col>

                            <v-icon id="usersection_chevron" class="pb-1 pl-2 pr-5">mdi-chevron-down</v-icon>
                        </v-row>
                    </button>
                </template>
                <v-list id="user-menu-list" role="menu" class="py-0">
                    <div class="usermenuheader">
                        <v-row class="py-0 pt-1 pl-2">
                            <v-col cols="3" class="py-0 pr-4">
                                <v-avatar size="38" class="pr-0 avat2">
                                    <span style="font-weight: bold">{{ initials }}</span>
                                </v-avatar>
                            </v-col>
                            <v-col class="idpart pl-1 pr-0">
                                <div class="namepart">
                                    <span> {{ decryptedName }}</span>
                                </div>
                                <div class="mailpart">
                                    <span> {{ decryptedMail }}</span>
                                </div>
                            </v-col>
                        </v-row>
                    </div>

                    <!-- Switch: Dark Mode -->
                    <v-list-item
                        role="menuitemcheckbox"
                        :aria-checked="user_settings.dark_mode ? 'true' : 'false'"
                        tabindex="0"
                        @click="
                            user_settings.dark_mode = !user_settings.dark_mode
                            changeTheme && changeTheme()
                        "
                        @keydown.enter.prevent="
                            user_settings.dark_mode = !user_settings.dark_mode
                            changeTheme && changeTheme()
                        "
                        @keydown.space.prevent="
                            user_settings.dark_mode = !user_settings.dark_mode
                            changeTheme && changeTheme()
                        ">
                        <v-list-item-action class="mr-1">
                            <!-- .stop: Klick auf den Switch toggelt nicht zusätzlich den List-Item-Click -->
                            <v-switch
                                class="ma-0"
                                hide-details
                                inset
                                prepend-icon="mdi-theme-light-dark"
                                v-model="user_settings.dark_mode"
                                color="darkgreen"
                                aria-label="Dark Mode"
                                @change="changeTheme"
                                @click.stop />
                        </v-list-item-action>
                        <v-list-item-content>
                            <v-list-item-title>Dark Mode</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>

                    <!-- Switch: Advanced Mode (nur wenn erlaubt) -->
                    <v-list-item
                        v-if="$helpers.isActionAllowed(42, user_rights)"
                        role="menuitemcheckbox"
                        :aria-checked="advanced_switch ? 'true' : 'false'"
                        tabindex="0"
                        @click="
                            advanced_switch = !advanced_switch
                            switchAdvancedMode && switchAdvancedMode()
                        "
                        @keydown.enter.prevent="
                            advanced_switch = !advanced_switch
                            switchAdvancedMode && switchAdvancedMode()
                        "
                        @keydown.space.prevent="
                            advanced_switch = !advanced_switch
                            switchAdvancedMode && switchAdvancedMode()
                        ">
                        <v-list-item-action class="mr-1">
                            <v-switch
                                class="ma-0"
                                hide-details
                                inset
                                prepend-icon="mdi-book-open"
                                v-model="advanced_switch"
                                color="darkgreen"
                                aria-label="Advanced Mode"
                                @change="switchAdvancedMode"
                                @click.stop />
                        </v-list-item-action>
                        <v-list-item-content>
                            <v-list-item-title>Advanced Mode</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>

                    <v-divider />

                    <!-- Logout als echtes Menü-Item -->
                    <v-list-item role="menuitem" tabindex="0" @click="$router.push('/logout')" @keydown.enter.prevent="$router.push('/logout')" @keydown.space.prevent="$router.push('/logout')">
                        <v-list-item-content>
                            <v-list-item-title><v-chip color="black" class="white--text" style="text-transform: uppercase; cursor: pointer">Logout</v-chip></v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </v-menu>
        </v-app-bar>

        <v-dialog
            id="endoftest-dialog"
            v-if="!$vuetify.breakpoint.smAndUp && !['/seo-ai', '/login'].includes($route.path)"
            v-model="mobileoverlay"
            width="100%"
            fullscreen
            style="margin: 0px !important"
            persistent
            role="dialog"
            aria-modal="true"
            aria-labelledby="maintenance-title">
            <div class="maincard">
                <div class="vh">
                    <div>
                        <div class="wrap">
                            <img src="./assets/maintenance.svg" width="200px" class="illustration" alt="Maintenance illustration" />
                            <h1 id="maintenance-title">Maintenance mode</h1>
                            <h2>
                                <p>Sorry for the inconvenience.<br />Quaros mobile version is currently undergoing maintenance.<br /><br /></p>
                            </h2>
                            <div class="msgbox">
                                <span class="text">Thank you for your understanding.</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </v-dialog>

        <v-main id="content" ref="main" tabindex="-1" :class="{ newbg: isSpecialRoute }" role="main" aria-label="Main content area">
            <KeepAlive max="4" include="Ideas,Suggest,KeywordPlanner,AdhocWdfIdf,KIChat" exclude="cs-editor,cs-projects">
                <router-view :key="routerViewKey" v-slot="{ Component }">
                    <transition name="fade">
                        <component :is="Component" />
                    </transition>
                </router-view>
            </KeepAlive>

            <!-- <router-view v-slot="{ Component, route }">
      <transition name="fade" mode="out-in">
        <keep-alive>
          <component v-if="route.matched.length > 0" :is="Component"></component>
          <div v-else>Not Found</div>
        </keep-alive>
      </transition>
    </router-view> -->
        </v-main>
        <dashboard-core-settings
            v-if="
                usermail &&
                $route.path !== '/content-projects' &&
                !$route.path.includes('/content-editor') &&
                !$route.path.includes('/wdf-idf') &&
                !$route.path.includes('/competitors') &&
                !$route.path.includes('/ai-suite') &&
                !$route.path.includes('/seo-ai')
            "
            v-model="expandOnHover" />
    </v-app>
</template>

<script>
    import Vue from "vue"

    export default {
        props: {
            source: String
        },
        components: {
            UpdateCard: () => import("./components/UpdateCard"),
            DashboardCoreSettings: () => import("./components/Settings"),
            Checkout: () => import("./components/checkout/Checkout"),
            Notifications: () => import("./components/Notifications"),
            CheckoutSuccess: () => import("./components/checkout/CheckoutSuccess"),
            CheckoutCanceled: () => import("./components/checkout/CheckoutCanceled"),
            CompanySwitch: () => import("./components/CompanySwitch"),
            KIChat: () => import("./components/KIChat")
            // OmrReview: () => import("./components/OmrReview")
        },

        data: () => ({
            mobileoverlay: true,
            // mode: false,
            advanced_switch: false,
            researchnav: false,
            sidenav_variant: true,
            showCheckoutSuccess: false,
            showUpdateInfo: false,
            swatches: [["#1cedb2", "#11bd8c", "#009f89"]],
            selectedColor: null,
            defaultPrimary: null,
            // themeswitch: false,
            companyGroupJoined: false,
            inactiveCheckDone: false,
            planChecked: false,
            logoSource: require("./assets/quaro_final_rgb.svg"),
            logoSourceLutz: require("./assets/quickseofinal2.jpg"),
            apollonsrc: require("./assets/apollon_logo.png"),
            tabItems: [
                {
                    name: "Advanced",
                    price: "229,99",
                    text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Maecenas ullamcorper diam congue felis consequat fringilla. Phasellus ac orci nec velit tristique lacinia quis in purus. Maecenas eu sagittis felis, id egestas augue. Cras eu sapien vehicula, cursus lectus vitae, efficitur nunc. Phasellus sodales faucibus mi non tincidunt. Orci varius natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Nulla accumsan arcu sed dictum mattis. Suspendisse in placerat nisi. Donec quis sapien ac diam interdum ullamcorper. Aenean blandit elit mi, a euismod tortor convallis vel. Aliquam nec urna dignissim, dignissim eros a, volutpat metus. Vestibulum vel placerat mi. Sed tincidunt hendrerit tellus eget condimentum. Phasellus pellentesque augue magna, nec elementum nunc rutrum sit amet. Donec lobortis ullamcorper laoreet. "
                },
                {
                    name: "Pro",
                    price: "479,99",
                    text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Maecenas ullamcorper diam congue felis consequat fringilla. Phasellus ac orci nec velit tristique lacinia quis in purus. Maecenas eu sagittis felis, id egestas augue. Cras eu sapien vehicula, cursus lectus vitae, efficitur nunc. Phasellus sodales faucibus mi non tincidunt. Orci varius natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Nulla accumsan arcu sed dictum mattis. Suspendisse in placerat nisi. Donec quis sapien ac diam interdum ullamcorper. Aenean blandit elit mi, a euismod tortor convallis vel. Aliquam nec urna dignissim, dignissim eros a, volutpat metus. Vestibulum vel placerat mi. Sed tincidunt hendrerit tellus eget condimentum. Phasellus pellentesque augue magna, nec elementum nunc rutrum sit amet. Donec lobortis ullamcorper laoreet. "
                },
                {
                    name: "Enterprise",
                    price: "889,99",
                    text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Maecenas ullamcorper diam congue felis consequat fringilla. Phasellus ac orci nec velit tristique lacinia quis in purus. Maecenas eu sagittis felis, id egestas augue. Cras eu sapien vehicula, cursus lectus vitae, efficitur nunc. Phasellus sodales faucibus mi non tincidunt. Orci varius natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Nulla accumsan arcu sed dictum mattis. Suspendisse in placerat nisi. Donec quis sapien ac diam interdum ullamcorper. Aenean blandit elit mi, a euismod tortor convallis vel. Aliquam nec urna dignissim, dignissim eros a, volutpat metus. Vestibulum vel placerat mi. Sed tincidunt hendrerit tellus eget condimentum. Phasellus pellentesque augue magna, nec elementum nunc rutrum sit amet. Donec lobortis ullamcorper laoreet. "
                }
            ],
            tab: null,
            query: false,
            expand: true,
            timer: 30,
            showUpdateMessage: false,
            expandOnHover: false,
            drawer: true,
            dropItems: [],
            get username() {
                if (localStorage.getItem("username")) {
                    if (localStorage.getItem("username") != "[object Object],[object Object]") {
                        return localStorage.getItem("username")
                    } else {
                        return
                    }
                } else {
                    return
                }
            },
            get usermail() {
                if (localStorage.getItem("user")) {
                    if (localStorage.getItem("user") != "[object Object],[object Object]") {
                        return localStorage.getItem("user")
                    } else {
                        return
                    }
                } else {
                    return
                }
            },
            auth: "Basic " + window.btoa(process.env.VUE_APP_AUTH),
            offline: false
        }),
        computed: {
            company_id() {
                return this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
            },
            internalSearchCustomerIds() {
                const defaults = ["2", "83", "1780", "1547"]
                const fromEnv = (process.env.VUE_APP_INTERNAL_SEARCH_CUSTOMER_IDS || "")
                    .split(",")
                    .map((x) => x.trim())
                    .filter(Boolean)

                return [...new Set([...defaults, ...fromEnv])]
            },
            showInternalSearchesNav() {
                return this.internalSearchCustomerIds.includes(String(this.company_id))
            },
            showContentSuiteNav() {
                return (
                    (this.$store.state.content_suite || this.$store.state.admin) &&
                    (this.$store.state.company_status_content_suite == "active" || this.$store.state.company_status_content_suite == "testing")
                )
            },
            showQuickSeoNav() {
                return (
                    (this.$store.state.monitoring_suite || this.$store.state.admin) &&
                    (this.$store.state.company_status_monitoring_suite == "active" || this.$store.state.company_status_monitoring_suite == "testing") &&
                    this.lutz
                )
            },
            showMonitoringNav() {
                return (
                    (this.$store.state.monitoring_suite || this.$store.state.admin) &&
                    (this.$store.state.company_status_monitoring_suite == "active" || this.$store.state.company_status_monitoring_suite == "testing")
                )
            },
            showAiSuiteNav() {
                return (
                    (this.$store.state.ai_suite || this.$store.state.admin) &&
                    (this.$store.state.company_status_ai_suite == "active" || this.$store.state.company_status_ai_suite == "testing" || this.$store.state.company_status_ai_suite == "free")
                )
            },
            showAiChatNav() {
                return (
                    (this.$store.state.ki_chat || this.$store.state.admin) &&
                    (this.$store.state.company_status_ki_chat == "active" || this.$store.state.company_status_ki_chat == "testing" || this.$store.state.company_status_ki_chat == "free")
                )
            },
            lutz() {
                return this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8) == 32
            },
            decryptedMail() {
                if (this.usermail.length > 0) {
                    return this.CryptoJS.AES.decrypt(this.usermail, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                } else {
                    return ""
                }
            },
            decryptedName() {
                if (this.username.length > 0) {
                    return this.CryptoJS.AES.decrypt(this.username, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                } else {
                    return ""
                }
            },
            initials() {
                let splittedName = this.decryptedName.split(" ")
                let initials = ""
                splittedName.forEach((namepart) => {
                    let firstletter = namepart.substring(0, 1)
                    initials += firstletter
                })
                return initials
            },
            user_rights() {
                return this.$store.state.user_rights
            },
            company_status() {
                return this.$store.state.company_status
            },
            company_status_content_suite() {
                return this.$store.state.company_status_content_suite
            },
            items() {
                return this.$store.state.nav_items
            },
            routerViewKey() {
                const path = this.$route?.path || ""
                if (path.startsWith("/ai-suite/project/")) {
                    return path
                }
                return this.$route.fullPath
            },
            isSpecialRoute() {
                return ["/seo-ai", "/ai-suite/setup"].includes(this.$route.path)
            },
            user_settings: {
                get: function () {
                    return this.$store.state.user_settings
                }
                // set: function (newValue) {
                //     this.$store.dispatch("CHANGE_USER_SETTINGS", newValue)
                // }
            }
        },

        methods: {
            focusMain() {
                this.$nextTick(() => {
                    const el = this.$refs.main && (this.$refs.main.$el || this.$refs.main)
                    if (el && typeof el.focus === "function") {
                        el.focus()
                    }
                })
            },
            switchAdvancedMode() {
                this.user_settings.advancedMode = !this.user_settings.advancedMode
                this.$store.dispatch("CHANGE_USER_SETTINGS", this.user_settings)
            },
            changeThemeColor() {
                this.$vuetify.theme.themes.light.primary = this.selectedColor
                this.$vuetify.theme.themes.dark.primary = this.selectedColor
            },
            togglePrimaryColor() {
                // auf Schwarz oder Default zurücksetzen
                this.selectedColor = this.user_settings.primary_black ? "#000000" : this.defaultPrimary || this.$vuetify.theme.themes.light.primary
                this.changeThemeColor()
                this.$store.dispatch("CHANGE_USER_SETTINGS", this.user_settings)
            },
            // NEU: Accessibility Mode kombiniert Schwarz + Bold
            toggleAccessibilityMode() {
                const on = this.user_settings.accessibility_mode === true
                if (typeof this.user_settings.bold_text === "undefined") {
                    this.$set(this.user_settings, "bold_text", false)
                }
                this.user_settings.bold_text = on
                this.user_settings.primary_black = on
                this.selectedColor = on ? "#000000" : this.defaultPrimary || this.$vuetify.theme.themes.light.primary
                this.changeThemeColor()
                // NEU: globale Schriftgröße um +30% (rem-basiert)
                document.documentElement.classList.toggle("a11y-fontscale", on)
                this.$store.dispatch("CHANGE_USER_SETTINGS", this.user_settings)
            },
            changeTheme() {
                this.$vuetify.theme.light = !this.$vuetify.theme.light
                this.$vuetify.theme.dark = !this.$vuetify.theme.dark

                this.$store.dispatch("CHANGE_USER_SETTINGS", this.user_settings)
            },
            changeSettings() {
                this.$store.dispatch("CHANGE_USER_SETTINGS", this.user_settings)
            },
            onCancel() {
                console.log("paypal payment canceled")
            },
            onError() {
                console.log("error on paypal-payment")
            },
            onApprove: function (data, actions) {
                alert("You have successfully created subscription " + data.subscriptionID)
            },
            createSubscriptionEnterprise: function (data, actions) {
                return actions.subscription.create({
                    /* Creates the subscription */
                    plan_id: "P-5AM96077DD428871BMBNF2BI"
                })
            },
            createSubscriptionPro: function (data, actions) {
                return actions.subscription.create({
                    /* Creates the subscription */
                    plan_id: "P-5AM96077DD428871BMBNF2BI"
                })
            },
            createSubscriptionAdvanced: function (data, actions) {
                return actions.subscription.create({
                    /* Creates the subscription */
                    plan_id: "P-5AM96077DD428871BMBNF2BI"
                })
            },
            clickNotification(notification) {
                if (notification.type == "2") {
                    this.$router.push("/brand-index/" + notification.listname)
                }
            },
            shownav() {
                if (this.drawer) {
                    this.expand = !this.expand
                } else {
                    this.drawer = !this.drawer
                }
            },
            idle() {},

            createNotification(data) {
                let notificationObject
                if (data.type == "0") {
                    //type 0 = error
                    notificationObject = {
                        type: data.type,
                        message: data.message,
                        icon: "mdi-alert-circle",
                        icon_color: "red",
                        listname: data.listname,
                        date: this.getDateTime(),
                        id: data.id
                    }
                    for (let index = 0; index < this.notifications.length; index++) {
                        if (this.notifications[index].id == data.id) {
                            this.notifications.splice(index, 1)
                            this.messages--
                        }
                    }
                } else if (data.type == "1") {
                    //type 1 = branddashboard is creating
                    notificationObject = {
                        type: data.type,
                        message: data.message,
                        icon: "mdi-clock",
                        icon_color: "orange",
                        progress: 0,
                        listname: data.listname,
                        date: this.getDateTime(),
                        id: data.id
                    }
                } else if (data.type == "2") {
                    //type 1 = branddashboard created
                    // remove id and push new notification
                    for (let index = 0; index < this.notifications.length; index++) {
                        if (this.notifications[index].id == data.id) {
                            this.notifications.splice(index, 1)
                            this.messages--
                        }
                    }
                    notificationObject = {
                        type: data.type,
                        message: data.message,
                        icon: "mdi-comment-check",
                        icon_color: "green",
                        listname: data.listname,
                        date: this.getDateTime(),
                        id: data.id
                    }
                } else if (data.type == "3") {
                    //type 3 = progress update
                    console.log(this.notifications)
                    for (let index = 0; index < this.notifications.length; index++) {
                        if (this.notifications[index].id == data.id) {
                            this.notifications[index].progress = data.progress
                            console.log(this.notifications)
                        }
                    }
                    return null
                }
                return notificationObject
            },
            getDateTime() {
                let dateTime = new Date()
                let month = dateTime.getMonth() + 1
                let day = dateTime.getDate()
                let hours = dateTime.getHours()
                let minutes = dateTime.getMinutes()
                return day + "." + month + " | " + hours + ":" + minutes
            },
            deleteNotification(id) {
                for (let index = 0; index < this.notifications.length; index++) {
                    if (this.notifications[index].id == id) {
                        this.notifications.splice(index, 1)
                        this.messages--
                    }
                }
            },
            resolve_img_url() {
                return "https://eu.ui-avatars.com/api/?name=" + this.decryptedPlan
            },
            ariaCurrent(to, exact = false) {
                try {
                    const path = typeof to === "object" ? this.$router.resolve(to).route.path : String(to || "")
                    return exact ? (this.$route.path === path ? "page" : null) : this.$route.path.startsWith(path) ? "page" : null
                } catch (e) {
                    return null
                }
            }
        },
        watch: {
            // mode(val) {
            //     console.log(val)
            //     setTimeout(() => {
            //         this.user_settings.advancedMode = this.mode
            //     }, 200)
            // },
            $route() {
                this.$nextTick(() => {
                    const el = this.$refs.main && (this.$refs.main.$el || this.$refs.main)
                    if (el && typeof el.focus === "function") {
                        el.focus()
                    }
                })
            },
            drawer(val) {
                if (!val) {
                    let checkbox = (document.getElementById("hamburg").checked = false)
                }
                return
            },
            "user_settings.advancedMode"(val) {
                this.advanced_switch = val
            },
            "user_settings.primary_black"(val) {
                // Fallback, falls extern geändert
                this.togglePrimaryColor()
            },
            // NEU: falls extern gesetzt
            "user_settings.accessibility_mode"(val) {
                this.toggleAccessibilityMode()
            }
        },
        created() {},
        updated() {
            if (!this.companyGroupJoined && localStorage.company_id) {
                this.$socket.emit("joinCompanyGroup", localStorage.company_id)
                this.companyGroupJoined = true
                // try {
                //     let decrypted_user_id = this.CryptoJS.AES.decrypt(localStorage.id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                //     const matomoTracker = this.$matomo.getTracker()
                //     matomoTracker.setUserId(decrypted_user_id)
                // } catch (error) {
                //     console.log(error)
                //     console.log("matomo blocked")
                // }
            }

            //inactive check - log out users of inactive companies
            if (!this.inactiveCheckDone && localStorage.company_id) {
                this.inactiveCheckDone = true
                this.$http.post(process.env.VUE_APP_APIURL + "/user/inactivecheck", { company_id: localStorage.company_id }, { headers: { Authorization: this.auth } }).then(function (response) {
                    if (!response.body.active) {
                        delete localStorage.token
                        delete localStorage.user
                        delete localStorage.id
                        delete localStorage.company_id
                        delete localStorage.plan
                        delete localStorage.username
                        this.$router.push("/login")
                    }
                })
            }
        },

        async mounted() {
            this.$router.onReady(() => {
                if (this.$route.query.checkout == "success") {
                    this.showCheckoutSuccess = true
                }
            })

            if (localStorage.user !== undefined) {
                await this.$store.dispatch("FETCH_STARTUP_DATA", this.CryptoJS.AES.decrypt(localStorage.user, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8))
                if (this.user_settings.dark_mode) {
                    this.$vuetify.theme.light = !this.$vuetify.theme.light
                    this.$vuetify.theme.dark = !this.$vuetify.theme.dark
                }
            }
            this.selectedColor = this.$vuetify.theme.themes.light.primary
            this.defaultPrimary = this.selectedColor

            // Defaults für neue Settings
            if (typeof this.user_settings.bold_text === "undefined") {
                this.$set(this.user_settings, "bold_text", false)
            }
            if (typeof this.user_settings.accessibility_mode === "undefined") {
                this.$set(this.user_settings, "accessibility_mode", false)
            }

            // Bisherige Speicherung respektieren
            if (this.user_settings.primary_black) {
                this.selectedColor = "#000000"
                this.changeThemeColor()
            }

            // Accessibility Mode erzwingen (setzt auch bold + schwarz)
            if (this.user_settings.accessibility_mode) {
                this.toggleAccessibilityMode() // setzt auch a11y-fontscale
            }

            this.$socket.on("showReloadmessage", (data) => {
                console.log("Refreshing frontend initiated...showing message")
                this.showUpdateMessage = true
                setInterval(() => {
                    if (this.timer > 0) {
                        this.timer--
                    }
                }, 1000)
            })
            this.$socket.on("reloadFrontend", (data) => {
                console.log("Refreshing frontend...")
                this.$router.go()
            })

            if (window.HubSpotConversations) {
                console.log("The api is ready already")
            } else {
                window.hsConversationsOnReady = [
                    () => {
                        console.log("Now the api is ready")
                    }
                ]
            }
            // Warten, bis das Pipedrive-Skript geladen ist
            window.addEventListener("load", function () {
                // Zugriff auf das Chat-Button-Element
                setTimeout(() => {
                    var chatButton = document.querySelector("#pipedrive-chat-holder > div > button") // Passen Sie die Selektor-Struktur entsprechend an
                    console.log("load event")
                    console.log(chatButton)
                    // Überprüfen, ob das C   hat-Button-Element gefunden wurde

                    if (chatButton) {
                        // Ändern der Hintergrundfarbe und Textfarbe
                        chatButton.style.backgroundColor = "#FF0000" // Ändern Sie die Hintergrundfarbe nach Ihren Wünschen
                        chatButton.style.color = "#FFFFFF" // Ändern Sie die Textfarbe nach Ihren Wünschen
                        // Weitere Stile können ebenfalls angepasst werden
                    }
                }, 1000)
            })
            window.addEventListener("offline", (event) => {
                console.log("off")
                this.offline = true
                // the user has lost connection here
            })
            window.addEventListener("online", (event) => {
                console.log("on")
                this.offline = false
                // the user has lost connection here
            })
        }
    }
</script>

<style lang="scss">
    @import "./assets/css/global.scss";
    @import "./assets/css/mobile.css";

    .navdivider {
        height: 0.1px;
        width: 72%;
        margin-left: auto;
        margin-right: auto;
        // border-radius: 2px;
        background-color: white;
        border-color: white;
        border: none;
    }

    .v-color-picker__controls {
        display: none !important;
    }
    .v-color-picker__swatch {
        flex-direction: row !important;
        margin-bottom: 0px !important;
        padding-bottom: 0px;
    }

    .usermenuactions {
        align-content: center;
    }
    .usermenulogoutbtn {
        margin-left: auto !important;
    }
    .notification:hover {
        background-color: #e9e9e9;
    }
    .nofitificationtext {
        font-family: Nunito;
    }
    #research_line_chart > #line_chart_card > div.v-card__text {
        height: 100% !important;
    }
    .advanced-mode {
        padding-top: 15px;
        // width: 97%;
        padding-left: 24px;
        padding-right: 24px;
        margin: auto;
        #line_chart_card > div.v-card__text {
            max-height: 350px !important;
            height: 30vh;
            padding-right: 40px !important;
        }

        .init-search-box {
            padding-top: 15px !important;
        }
        #serp_items_table_filter {
            position: absolute;
            bottom: 10px;
            right: 0px;
        }
        #keyword-table table > tbody > tr > td,
        #keyword-table table > thead > tr > th {
            padding: 0px 8px;
        }
        #keyword-table table > tbody > tr > td:nth-child(1),
        #keyword-table table > thead > tr > th:nth-child(1) {
            padding: 0px 16px;
        }
        #trend_table table > tbody > tr > td,
        #trend_table table > thead > tr > th {
            padding: 0px 8px;
        }
        #trend_table table > tbody > tr > td:nth-child(1),
        #trend_table table > thead > tr > th:nth-child(1) {
            padding: 0px 16px;
        }
    }
    .standard-mode {
        padding-top: 30px;
        // max-width: 90%;
        padding-left: 100px;
        padding-right: 100px;
        margin: auto;
    }

    @media only screen and (max-width: 1500px) {
        .standard-mode {
            padding-left: 50px;
            padding-right: 50px;
        }
    }
    @media only screen and (max-width: 800px) {
        .standard-mode {
            padding-left: 15px;
            padding-right: 15px;
        }
    }
    // @media screen and (max-height: 1173px) {
    //     #linechart_col {
    //         max-height: 30vh;
    //     }
    // }
    // @media screen and (min-height: 1173px) {
    //     #linechart_col {
    //         height: 36vh;
    //     }
    //     #line_chart_card > div.v-card__text {
    //         flex-grow: 1;
    //         min-height: 0;
    //         // height: 85%;
    //         > div {
    //             position: relative;
    //             height: 95%;
    //         }
    //     }
    // }
    #mobileOverlay {
        // z-index: 9999;
    }
    .tooltitle-btn {
        background: none;
        border: 0;
        padding: 0;
        display: inline-flex;
        align-items: center;
        padding-top: 7px;
    }
    .theme--light.v-label {
        color: rgba(0, 0, 0, 0.8);
    }
</style>
<style scoped lang="scss">
    .newbg {
        /* oder ein anderes Hintergrundbild, Gradient etc. */

        background-image: url("./assets/bg-quaro-hero.png") !important;
        background-size: cover !important;
        background-position: center !important; // <-- Bild 56px nach rechts verschieben /* Center the image */
        background-repeat: no-repeat !important; /* Do not repeat the image */
        min-height: 100vh; // <-- Ergänzung
        width: 100%;
        height: 100%;
    }

    // @media (max-width: 1265px) {
    //     .newbg {
    //         background-position: center !important;
    //     }
    // }

    .main {
        width: 100%;
        height: 100%;
    }
    .maincard {
        text-align: center;
        margin: 0px;
        padding: 0px;
        height: 100%;
        color: #fff;
        background: linear-gradient(-45deg, #ee7752, #e73c7e, #23a6d5, #23d5ab);
        background-size: 400% 400%;
        -webkit-animation: Gradient 15s ease infinite;
        -moz-animation: Gradient 15s ease infinite;
        animation: Gradient 15s ease infinite;
    }
    .vh {
        height: 100%;
        align-items: center;
        display: flex;
    }
    .vh > div {
        width: 100%;
        text-align: center;
        vertical-align: middle;
    }
    img {
        max-width: 100%;
    }
    .wrap {
        margin-top: 50px;
        text-align: center;
    }
    .wrap h1 {
        font-size: 30px;
        font-weight: 700;
        margin: 0 0 90px;
    }
    .wrap h2 {
        font-size: 24px;
        font-weight: 400;
        line-height: 1.6;
        margin: 0 0 80px;
    }
    .msgbox {
        display: flex;
        margin-bottom: 50px;
        .text {
            border: 1px dashed rgb(253, 253, 253);
            border-radius: 5px;
            padding: 2px 7px 2px 7px;
            margin-left: auto;
            margin-right: auto;
        }
    }
    // @-webkit-keyframes Gradient {
    //     0% {
    //         background-position: 0% 50%;
    //     }
    //     50% {
    //         background-position: 100% 50%;
    //     }
    //     100% {
    //         background-position: 0% 50%;
    //     }
    // }
    // @-moz-keyframes Gradient {
    //     0% {
    //         background-position: 0% 50%;
    //     }
    //     50% {
    //         background-position: 100% 50%;
    //     }
    //     100% {
    //         background-position: 0% 50%;
    //     }
    // }
    // @keyframes Gradient {
    //     0% {
    //         background-position: 0% 50%;
    //     }
    //     50% {
    //         background-position: 100% 50%;
    //     }
    //     100% {
    //         background-position: 0% 50%;
    //     }
    // }
    #logo-div {
        width: 130px;
        display: flex;
    }
    #logo {
        margin-top: auto;
        margin-bottom: auto;
    }
    #updatebtn {
        margin-left: 35px;
        // position: absolute;
        // top: 10px;
        // left: 220px;
        background: rgb(28, 237, 179);
        // background: linear-gradient(90deg, rgb(237, 28, 77) 0%, rgba(255, 152, 152, 1) 75%);
        background: linear-gradient(90deg, #009f89 0%, #e68989 75%);
    }
    #notificationline {
        margin-left: 60px !important;
        border-radius: 5px;
        border: 1px solid rgb(228, 228, 228);
    }
    .message {
        font-size: 0.9em;
    }
    .date {
        font-size: 0.8em;
    }
    .listname {
        font-weight: bold;
        font-size: 0.9em;
    }
    .v-badge__badge {
        margin-top: 20px;
    }
    .v-list-group__header {
        height: 100px !important;
    }
    img {
        width: 100%;
        height: auto;
    }
    #logotext {
        max-width: 150px;
        padding-left: 10px;
        margin-top: 1px !important;
    }
    #sidebarLogo {
        margin-left: 5px;
        margin-right: 5px;
        max-width: 50px;
    }
    .sidebarLogo:hover {
        cursor: pointer;
    }
    // background: "rgb(245, 254, 251)",
    //             background_gradient_1:"rgba(245, 254, 251, 1)",
    //             background_gradient_2:"rgba(247, 252, 251, 1)",
    //             background_gradient_3:"rgba(250, 250, 250, 1)",
    //             background_gradient_4:"rgba(249, 245, 247, 1)",
    //             background_gradient_5:"rgba(250, 244, 247, 1)",
    #content {
        background: var(--v-background-base);
        background: linear-gradient(
            90deg,
            var(--v-background_gradient_1-base) 0%,
            var(--v-background_gradient_2-base) 26%,
            var(--v-background_gradient_3-base) 50%,
            var(--v-background_gradient_4-base) 75%,
            var(--v-background_gradient_5-base) 100%
        );
        z-index: 0;
    }
    #contentcontainter {
        margin: auto;
    }
    #subnavIcon {
        padding-left: 39px;
        padding-right: 0px;
        margin-right: 10px;
    }
    #subnavText {
        padding-left: 0px !important;
    }
    .tooltitle:hover {
        cursor: pointer !important;
    }

    .v-application {
        font-family: "Nunito";
    }
    #usersection {
        display: inline-flex !important;
        align-items: center;
        height: 48px;
        margin-top: auto;
        margin-bottom: auto;
        padding-left: 6px;
        padding-right: 0px;
        border-radius: 12px;
        transition: background-color 0.18s ease;
    }
    #usersection:hover {
        background: rgba(0, 0, 0, 0.05);
    }
    .theme--dark #usersection:hover {
        background: rgba(255, 255, 255, 0.08);
    }

    .initials {
        color: white;
        font-weight: bold;
    }
    .vl {
        padding: 5px;
        padding-right: 7px;
        border-left: 1px solid rgba(0, 0, 0, 0.12);
        height: 55%;
        margin-top: auto;
        margin-bottom: auto;
    }
    .theme--dark .vl {
        border-left-color: rgba(255, 255, 255, 0.15);
    }
    .namepart {
        padding-left: 0px;
        font-size: 0.9em;
        white-space: nowrap;
    }
    .mailpart {
        padding-bottom: 10px;
        font-size: 0.75em;
        white-space: nowrap;
    }
    .idpart {
        padding-top: 3px !important;
        padding-left: 0px;
    }
    .avat {
        background: linear-gradient(56deg, var(--v-left_nav_gradient1-base) 0%, var(--v-left_nav_gradient3-base) 100%);

        // margin-top: 1px;
    }
    .usermenuheader {
        width: 100%;
        height: 55px;
        background: linear-gradient(56deg, var(--v-left_nav_gradient1-base) 0%, var(--v-left_nav_gradient3-base) 100%);
        color: white;
    }
    .avat2 {
        background: white;
        margin-top: 4px;
        color: var(--v-primary-base);
    }
    #userdata_nav {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    }
    #userdata_nav .namepart span,
    #userdata_nav .mailpart span {
        display: block; /* beide beginnen exakt an der gleichen Kante */
    }
    #userdata_nav .namepart {
        // margin-bottom: 2px;
    } /* optionaler Abstand */

    #nav2 {
        backdrop-filter: blur(12px);
        background: rgba(250, 251, 253, 0.78);
        box-shadow: 0 1px 0 rgba(0, 0, 0, 0.06), 0 4px 16px rgba(15, 23, 42, 0.05) !important;
    }
    .theme--dark #nav2 {
        background: rgba(40, 40, 40, 0.78);
        box-shadow: 0 1px 0 rgba(255, 255, 255, 0.08), 0 4px 16px rgba(0, 0, 0, 0.35) !important;
    }
    #nav {
        // background: rgb(1, 158, 134);
        z-index: 9;
        background: linear-gradient(180deg, var(--v-left_nav_gradient1-base) 0%, var(--v-left_nav_gradient2-base) 59%, var(--v-left_nav_gradient3-base) 100%);
        // background: linear-gradient(180deg, #028672 0%, #028671 59%, #064940 100%);
    }
    /* Trennlinie nur ZWISCHEN zwei sichtbaren Sektionen (v-if entfernt versteckte komplett),
       daher nie ein Streifen ohne zugehörige Suite und nie ein führender/abschließender Streifen. */
    #nav .nav-section + .nav-section::before {
        content: "";
        display: block;
        height: 1px;
        background: rgba(255, 255, 255, 0.1);
        margin: 6px 14px;
    }
    #nav.v-navigation-drawer--mini-variant .nav-section + .nav-section::before {
        margin: 6px 8px;
    }
    #nav .navitem {
        border-radius: 10px;
        margin: 2px 10px;
        min-height: 40px;
        transition: background-color 0.18s ease;
    }
    #nav .navitem:hover {
        background: rgba(255, 255, 255, 0.08);
    }
    #nav .navitem.v-list-item--active {
        background: rgba(255, 255, 255, 0.16);
    }
    #nav .navitem.v-list-item--active::before {
        opacity: 0 !important;
    }
    #nav.v-navigation-drawer--mini-variant .navitem {
        margin: 2px 0;
        border-radius: 0;
    }
    // .theme--dark #nav {
    //     background: linear-gradient(180deg, var(--v-left_nav_gradient1-base) 0%, #004339 59%, #001f1b 100%);
    // }

    .subnavtext {
        font-size: 12px;
    }

    // -BUGRER MENU START

    label.hamburg {
        display: block;
        // background: #555;
        width: 22px;
        height: 16px;
        position: relative;
        margin: auto;
        border-radius: 4px;
    }
    label.hamburg {
        cursor: pointer;
    }
    input#hamburg {
        /* statt display:none -> visuell versteckt, aber fokussierbar */
        position: absolute;
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
        border: 0;
    }

    .line {
        position: absolute;
        // left: 10px;
        height: 3px;
        width: 100%;
        background: var(--v-primary-base);
        border-radius: 2px;
        display: block;
        transition: 0.5s;
        transform-origin: center;
    }

    .line:nth-child(1) {
        top: 0px;
    }
    .line:nth-child(2) {
        top: 7px;
    }
    .line:nth-child(3) {
        bottom: -1px;
    }

    #hamburg:checked + .hamburg .line:nth-child(1) {
        transform: translateY(8px) rotate(-45deg);
    }

    #hamburg:checked + .hamburg .line:nth-child(2) {
        opacity: 0;
    }

    #hamburg:checked + .hamburg .line:nth-child(3) {
        transform: translateY(-8px) rotate(45deg);
    }
    .burger-wrapper {
        display: flex;
        width: 55px;
        height: 98%;
        margin-left: -15px;
    }
    .v-toolbar__content {
        padding: 4px 0px !important;
    }

    //BURGER MENU END

    .switch-holder {
        margin: 8px 10px 4px 10px;
        background-color: rgba(255, 255, 255, 0.09);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 10px;
        display: flex;
        padding: 6px 10px;
        transition: background-color 0.18s ease;
    }
    .switch-holder:hover {
        background-color: rgba(255, 255, 255, 0.14);
    }
    #nav.v-navigation-drawer--mini-variant .switch-holder {
        margin: 8px 4px 4px 4px;
        padding: 4px 0;
        justify-content: center;
        overflow: hidden;
    }
    .switch-holder--active,
    .switch-holder--active:hover {
        background-color: rgba(255, 255, 255, 0.18);
        border-color: rgba(255, 255, 255, 0.4);
    }
    .switch-holder--active .switch-label {
        color: #ffffff;
    }
    .switch-holder-expand {
        padding: 6px 10px;
    }
    .mode_switch {
        padding-bottom: 1px;
    }
    .switchpadding {
        padding-left: 9px !important;
    }
    .switch-label {
        padding: 0;
        color: rgba(255, 255, 255, 0.92);
        text-transform: uppercase;
        font-size: 0.7em;
        font-weight: bolder;
        letter-spacing: 0.06em;
        min-width: 100px !important;
        padding-top: 3px !important;
        display: none;
        margin-top: auto;
        margin-bottom: auto;
    }
    .expand {
        display: flex !important;
    }
    #nav:hover .switch-label {
        display: flex !important;
    }
    .betachip {
        font-weight: bold !important;
    }
    .v-list-item__icon {
        margin-left: -22px !important;
    }
    .v-list-group__header__append-icon {
        margin-left: 0px !important;
    }
    #gradient {
        background: rgb(28, 237, 179);
        background: linear-gradient(90deg, #009f89 0%, #e68989 75%);
    }
</style>
<style lang="scss">
    .cs_nav > div.v-list-group__header.v-list-item.v-list-item--link.theme--dark > div.v-list-item__icon.v-list-group__header__append-icon {
        margin-left: 0px;
        min-width: 24px !important;
    }
    #pipedrive-chat-holder > div > button {
        background: white !important;
    }

    /* --- Side nav polish (drawer internals, must be unscoped) --- */
    #nav .v-navigation-drawer__content {
        padding-top: 8px;
    }
    #nav .v-navigation-drawer__content::-webkit-scrollbar {
        width: 4px;
    }
    #nav .v-navigation-drawer__content::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.25);
        border-radius: 2px;
    }
    #nav .v-list-item__title {
        font-size: 13.5px;
        letter-spacing: 0.2px;
    }
    #nav .navitem.v-list-item--active .v-list-item__title {
        font-weight: 700;
    }
    #nav .v-list-group__header {
        border-radius: 10px;
        margin: 2px 10px;
        transition: background-color 0.18s ease;
    }
    #nav .v-list-group__header:hover {
        background: rgba(255, 255, 255, 0.08);
    }
    #nav.v-navigation-drawer--mini-variant .v-list-group__header {
        margin: 2px 0;
        border-radius: 0;
    }
    #nav .v-list-group--active > .v-list-group__header {
        background: rgba(255, 255, 255, 0.06);
    }
    #nav .v-navigation-drawer__append {
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        padding-top: 4px;
    }
    /* Advanced-Mode-Switch: klare Ein/Aus-Zustände auf dunklem Verlauf */
    #nav .mode_switch .v-input--switch__track {
        background-color: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.4);
        color: transparent !important;
        opacity: 1;
    }
    #nav .mode_switch .v-input--switch__thumb {
        background-color: #ffffff;
        color: #ffffff !important;
    }
    #nav .mode_switch.v-input--is-label-active .v-input--switch__track {
        background-color: #ffffff;
        border-color: #ffffff;
    }
    #nav .mode_switch.v-input--is-label-active .v-input--switch__thumb {
        background-color: var(--v-left_nav_gradient3-base);
        color: var(--v-left_nav_gradient3-base) !important;
    }
    /* Zugeklappte Nav: Switch verkleinert und zentriert, damit er in die 56px-Schiene passt */
    #nav.v-navigation-drawer--mini-variant .mode_switch {
        transform: scale(0.8);
        transform-origin: center center;
        flex: 0 0 auto;
        margin: 0 auto;
        padding: 0;
    }
    #nav.v-navigation-drawer--mini-variant .mode_switch .v-input__slot {
        justify-content: center;
        margin: 0;
    }
    #nav.v-navigation-drawer--mini-variant .mode_switch .v-input--selection-controls__input {
        /* Vuetifys Inset-Track ragt 4px nach links (left: -4px) — hier ausgeglichen:
           Flex-Centering halbiert die Margin-Wirkung, scale(0.8) skaliert sie: 10px * 0.5 * 0.8 = 4px */
        margin-right: 0;
        margin-left: 10px;
    }
</style>

<style lang="scss">
    /* Global Bold für Accessibility Mode (Icons ausgenommen) */
    .global-bold,
    .global-bold *:not(.v-icon):not(i.mdi):not(i[class*="mdi-"]):not(.material-icons) {
        font-weight: 700 !important;
    }
    .global-bold .v-icon,
    .global-bold .material-icons,
    .global-bold i.mdi,
    .global-bold i[class*="mdi-"] {
        font-weight: normal !important;
    }
</style>

<style lang="scss">
    /* +30% Basisschriftgröße für Accessibility Mode (wirkt auf rem) */
    html.a11y-fontscale {
        font-size: 130%;
    }
</style>
<style>
    .user-activator:focus-visible,
    #hamburg:focus-visible + .hamburg,
    .v-list-item:focus-visible,
    button:focus-visible,
    a:focus-visible,
    [role="button"]:focus-visible,
    #settings:focus-visible {
        outline: 3px solid var(--v-primary-base);
        outline-offset: 2px;
    }
</style>
